/**
 * PermissionGate — conditionally renders children based on the current
 * user's effective permissions.
 *
 * Usage (hide entirely):
 *   <PermissionGate action="create" subject="ADMIN">
 *     <CreateAdminButton />
 *   </PermissionGate>
 *
 * Usage (show disabled state):
 *   <PermissionGate
 *     action="delete"
 *     subject="CAMPAIGN"
 *     fallback={<Button disabled>Delete</Button>}
 *   >
 *     <Button onClick={handleDelete}>Delete</Button>
 *   </PermissionGate>
 *
 * Usage (a client's own action, which GFG staff must impersonate to perform):
 *   <PermissionGate action="create" subject="CAMPAIGN" clientOnly>
 *     <SendGiftsButton />
 *   </PermissionGate>
 */
import type { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import type { AccessScope } from "@/interfaces/permissions";
import { passesPermissionGate } from "@/utils/permission-helper";

interface PermissionGateProps {
  /** The action to check (e.g. "delete"). */
  action: string;
  /** The subject to check against (e.g. "CAMPAIGN"). */
  subject: string;
  /** Optional minimum scope — e.g. "all" gates a GFG-only surface. */
  scope?: AccessScope;
  /** Rendered when the user has the required permission. */
  children: ReactNode;
  /**
   * Additionally require that this is NOT a GFG staff session.
   *
   * For surfaces that belong to a client and that GFG performs BY impersonating one, never from
   * their own console. The capability check alone cannot express that: GFG admins genuinely hold
   * these caps at scope "all", which is what lets them act once impersonating.
   *
   * Impersonation satisfies it with nothing extra. Clerk's actor token makes the session the
   * target user, so `isGfg` — membership level ALL, resolved server-side — is false for the
   * duration, and the surface returns on its own.
   */
  clientOnly?: boolean;
  /** Rendered when the user lacks the required permission. Defaults to null (hidden). */
  fallback?: ReactNode;
}

export function PermissionGate({
  action,
  subject,
  scope,
  clientOnly = false,
  children,
  fallback = null,
}: PermissionGateProps) {
  const { can, isGfg, isLoaded } = usePermissions();

  if (!isLoaded) return null;

  const allowed = passesPermissionGate({
    permitted: can(action, subject, scope),
    clientOnly,
    isGfg,
  });
  return allowed ? <>{children}</> : <>{fallback}</>;
}
